import type { Editor } from '@tiptap/react';

const AI_CARET_SELECTOR = '.tiptap-ai-caret';
const SCROLL_THROTTLE_MS = 200;

export type AutoScrollRegistration = {
  cleanup: () => void;
  reengage: () => void; // Resumes following the caret after the user disengaged (e.g. when they return to the note)
};

type AiCaretPositionChangedPayload = { position: number };

export function registerAutoScroll(editor: Editor): AutoScrollRegistration | undefined {
  const editorDom = editor.view?.dom;
  if (!editorDom) return undefined;

  let userDisengaged = false;
  let lastScrollTime = 0;
  let aiCaretListenerSubscribed = false;

  const unsubscribeAiCaretListener = () => {
    if (!aiCaretListenerSubscribed) return;

    editor.off('aiCaret:positionChanged', onAiCaretPositionChanged);
    aiCaretListenerSubscribed = false;
  };

  const reengage = () => {
    userDisengaged = false;
  };

  const disengage = () => {
    if (userDisengaged) return;
    userDisengaged = true;
  };

  window.addEventListener('wheel', disengage, { passive: true });
  window.addEventListener('touchmove', disengage, { passive: true });

  const onAiCaretPositionChanged = ({ position }: AiCaretPositionChangedPayload) => {
    const now = performance.now();

    if (!userDisengaged && now - lastScrollTime >= SCROLL_THROTTLE_MS) {
      let target: Element | null = null;
      try {
        const nodeAtPosition = editor.view.domAtPos(position).node;
        target = nodeAtPosition instanceof Element ? nodeAtPosition : nodeAtPosition.parentElement;
      } catch {
        // Position can be transiently invalid during streamed updates.
      }

      if (target && editorDom.contains(target)) {
        lastScrollTime = now;
        target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        return;
      }

      // Fallback: if position->DOM resolution is temporarily unavailable follow the rendered AI caret element instead
      const caret = editorDom.querySelector(AI_CARET_SELECTOR);
      if (caret) {
        lastScrollTime = now;
        caret.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  };
  editor.on('aiCaret:positionChanged', onAiCaretPositionChanged);
  aiCaretListenerSubscribed = true;

  return {
    reengage,
    cleanup: () => {
      unsubscribeAiCaretListener();
      window.removeEventListener('wheel', disengage);
      window.removeEventListener('touchmove', disengage);
    },
  };
}
